import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import { errorNormalizer } from '../../../../../shared/Helpers/functions';
import { CustomButton, Text } from '../../../../../shared/uiComponents';
import { useFetchDTTTargetHistory, useFetchTATargetHistory } from './hooks';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { saveDTTSessionInfo, saveTASessionInfo } from '../../../../../redux/State/clientSlice/dataCollectionSlice';
import { getSessionTargets } from '../../../../../redux/State/clientSlice/sessionSlice';
import {
	StepProperties,
	TargetTypes,
	UpdateTargetStepsProperties,
} from '../../../../../redux/API/ClientAPIHelpers/programTargetsProperties';

export interface TargetDataProperties {
	targetId: string;
	targetInstructions: string;
	targetSteps: UpdateTargetStepsProperties[];
	targetType: TargetTypes;
}

export const DTTTargetActions = ({ targetId }: { targetId: string }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const { sessionId } = useParams();
	const [promptId, setPromptId] = useState<string>('');

	const promptsData = useSelector((state) => state.dataCollection.promptCodes);

	const { fetchHistory } = useFetchDTTTargetHistory(targetId);

	const fetchActiveTargets = useCallback(() => {
		if (!sessionId) return;
		dispatch(getSessionTargets(sessionId));
	}, [sessionId, dispatch]);

	const onClickHandler = (promptCodeId: string) => {
		if (!promptCodeId || !sessionId || !targetId) return;
		setPromptId(promptCodeId);
		dispatch(saveDTTSessionInfo({ promptCodeId, sessionId, targetId }))
			.then(unwrapResult)
			.then(() => {
				fetchHistory();
				fetchActiveTargets();
			})
			.catch(errorNormalizer)
			.finally(() => setPromptId(''));
	};

	return (
		<div className="targetAction">
			<div className="options">
				{promptsData.map((item, index) => (
					<CustomButton
						title={item.label}
						key={index}
						buttonType={index ? 'primary' : 'secondary'}
						onClick={() => onClickHandler(item.id)}
						className={`option ${item.id === promptId ? 'blink' : ''}`}
						style={{ height: '100%', fontSize: '36px' }}
					/>
				))}
			</div>
		</div>
	);
};

const TAPrompt = ({ step, targetId }: { step: UpdateTargetStepsProperties; targetId: string }) => {
	const { sessionId } = useParams();
	const dispatch = useDispatch<DispatchProperties>();
	const { fetchHistory } = useFetchTATargetHistory(targetId);

	const [promptId, setPromptId] = useState<string>('');
	const promptsData = useSelector((state) => state.dataCollection.promptCodes);

	const fetchActiveTargets = useCallback(() => {
		if (!sessionId) return;
		dispatch(getSessionTargets(sessionId));
	}, [sessionId, dispatch]);

	const onButtonClick = (promptCodeId: string) => {
		const data: StepProperties = {
			promptCodeId,
			stepId: step.id,
		};
		if (!sessionId || !targetId) return;
		setPromptId(promptCodeId);
		dispatch(saveTASessionInfo({ responses: [data], sessionId, targetId }))
			.then(unwrapResult)
			.then(() => {
				fetchHistory();
				fetchActiveTargets();
			})
			.catch(errorNormalizer)
			.finally(() => setPromptId(''));
	};

	return (
		<>
			<Text title={step.name} type="psm" fontWeight="semiBold" />
			<div className="options">
				{promptsData.map((item, index) => (
					<CustomButton
						title={item.label}
						key={index}
						buttonType={index ? 'primary' : 'secondary'}
						onClick={() => onButtonClick(item.id)}
						className={`option ${item.id === promptId ? 'blink' : ''}`}
						style={{ height: '100%', fontSize: '36px' }}
					/>
				))}
			</div>
		</>
	);
};

export const TATargetActions = ({
	targetId,
	steps,
}: {
	programId: string;
	steps: UpdateTargetStepsProperties[] | null;
	targetId: string;
}) => (
	<>
		{steps?.map((step, index) => (
			<div className="targetAction" key={`${targetId} + ${step.id}`}>
				<TAPrompt key={index} step={step} targetId={targetId} />
			</div>
		))}
	</>
);
